import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguageSwitch } from '@/hooks/useLanguage';
import { useSiteImage } from '@/hooks/useSiteImage';
import { useFooterData } from '@/hooks/useHeaderFooterLinks';

const SOCIAL_ICON_CLASS: Record<string, string> = {
  instagram: 'ri-instagram-line',
  facebook: 'ri-facebook-fill',
  youtube: 'ri-youtube-line',
  tiktok: 'ri-tiktok-line',
};

export default function SharedFooter() {
  const { t } = useTranslation();
  const { currentLang } = useLanguageSwitch();
  const prefix = currentLang === 'ko' ? '/ko' : currentLang === 'bn' ? '/bn' : '';
  const brandLogoUrl = useSiteImage(
    'footer.logo',
    '/brand/rxk-logo-footer.png',
  );
  const parentLogoUrl = useSiteImage(
    'footer.parentLogo',
    'https://api.thekseoul.com/storage/v1/object/public/rxk-content/legacy/3c5db519-2e35-47da-80f5-87ad39c44364_The.K-Seoul-Logo-Sqaure_1_d.png',
  );
  const paymentBannerUrl = useSiteImage('footer.paymentBanner', '/payment-methods-banner.png');
  const { columns: footerColumns, social: socialLinks } = useFooterData();

  return (
    <footer className="w-full bg-[#1A1A1A] pt-20 pb-10 px-6 md:px-12 lg:px-20">
      <div className="max-w-7xl mx-auto">
        {/* Main footer grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 mb-16 pb-16 border-b border-white/10">
          {/* Column 1: Brand */}
          <div className="flex flex-col gap-5">
            <div className="flex items-center">
              <img
                src={brandLogoUrl}
                alt="Rx.K Seoul"
                className="h-12 w-auto"
              />
            </div>
            <p className="text-xs text-white/40 font-light leading-[1.9]">
              {t('footer.brandDesc')}
            </p>
            <a
              href={`tel:${t('footer.hotline').replace(/[^\d+]/g, '')}`}
              className="flex items-center gap-2 text-xs text-white/70 hover:text-white transition-colors w-fit"
            >
              <i className="ri-phone-line text-sm"></i>
              {t('footer.hotlineLabel')}: {t('footer.hotline')}
            </a>
            <div className="flex gap-3 mt-1">
              {socialLinks.map((s) => (
                <a
                  key={s.id}
                  href={s.url}
                  rel="noopener noreferrer"
                  target="_blank"
                  aria-label={s.platform}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-[#7B1113] transition-colors cursor-pointer"
                >
                  <i className={`${SOCIAL_ICON_CLASS[s.platform] ?? 'ri-links-line'} text-white text-sm`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Columns 2-4: admin-managed link columns */}
          {footerColumns.map((col) => (
            <div key={col.id} className="flex flex-col gap-4">
              <p className="text-[11px] tracking-[0.2em] uppercase font-semibold text-white/50">
                {col.title[currentLang] || col.title.en}
              </p>
              <div className="flex flex-col gap-2.5">
                {col.links.map((item) =>
                  item.is_external ? (
                    <a
                      key={item.id}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-white/30 font-light hover:text-white/70 transition-colors"
                    >
                      {item.label[currentLang] || item.label.en}
                    </a>
                  ) : (
                    <Link
                      key={item.id}
                      to={prefix + item.href}
                      className="text-xs text-white/30 font-light hover:text-white/70 transition-colors"
                    >
                      {item.label[currentLang] || item.label.en}
                    </Link>
                  ),
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Company Disclosure + Payment Methods */}
        <div className="mb-8 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div className="flex flex-col gap-1">
            <p className="text-[10px] text-white/20 font-light leading-relaxed">
              {t('footer.marketingAuth')}
            </p>
            <p className="text-[10px] text-white/20 font-light leading-relaxed">
              {t('footer.manufacturer')}
            </p>
            <p className="text-[10px] text-white/20 font-light leading-relaxed">
              {t('footer.bdOperations')}
            </p>
            <p className="text-[10px] text-white/20 font-light leading-relaxed">
              {t('footer.registeredAddress')}
            </p>
            <p className="text-[10px] text-white/20 font-light leading-relaxed">
              {t('footer.tradeLicense')}
            </p>
          </div>
          <img
            src={paymentBannerUrl}
            alt="Accepted payment methods — Verified by SSLCommerz"
            className="h-auto w-full max-w-sm lg:max-w-md opacity-90"
          />
        </div>

        {/* Bottom bar */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-[11px] text-white/25 font-light">
            {t('footer.copyright')}
          </p>
          <div className="flex items-center gap-4 flex-wrap justify-center">
            <span className="text-[11px] text-white/25 font-light">
              {t('footer.dgda')}
            </span>
            <span className="text-white/10">|</span>
            <Link
              to={prefix + '/privacy-policy'}
              className="text-[11px] text-white/25 font-light hover:text-white/50 transition-colors"
            >
              {t('footer.privacyPolicy')}
            </Link>
            <span className="text-white/10">|</span>
            <Link
              to={prefix + '/terms-of-service'}
              className="text-[11px] text-white/25 font-light hover:text-white/50 transition-colors"
            >
              {t('footer.termsOfService')}
            </Link>
            <span className="text-white/10">|</span>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-white/25 font-light">{t('footer.parentCompany')}</span>
              <img
                src={parentLogoUrl}
                alt="The.K Seoul Co., Ltd."
                className="h-5 w-auto opacity-60"
              />
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}